/* ==========================================================================
   The Real Cost of a Dance Season: calculation engine
   Defaults come from published price ranges; see the Sources section on the
   page for the citation behind every default.
   ========================================================================== */

(function () {
  'use strict';

  var usd = function (n) {
    return '$' + Math.round(n).toLocaleString('en-US');
  };
  var usdc = function (n) {
    return '$' + Math.round(n).toLocaleString('en-US');
  };

  /* ---------------- Control definitions ---------------- */
  var GROUPS = [
    {
      n: '01',
      title: 'Studio &amp; training',
      note: 'The baseline you pay before a single competition is entered.',
      controls: [
        { id: 'dancers', label: 'Dancers in your family', min: 1, max: 4, step: 1, value: 1, fmt: 'int',
          hint: 'Per-dancer costs below are multiplied by this. Travel, tickets and photo packages are counted once for the household.' },
        { id: 'tuition', label: 'Monthly tuition, per dancer', min: 100, max: 700, step: 10, value: 250, fmt: 'usd',
          hint: 'Competitive-team tuition commonly runs $150&ndash;$500 a month depending on hours.' },
        { id: 'months', label: 'Months in the season', min: 6, max: 12, step: 1, value: 10, fmt: 'mo',
          hint: 'Most competition seasons run September through June.' },
        { id: 'privates', label: 'Private lessons per month', min: 0, max: 8, step: 1, value: 0, fmt: 'int',
          hint: 'Billed here at $45 per 30-minute private.' },
        { id: 'conventions', label: 'Conventions this season', min: 0, max: 4, step: 1, value: 1, fmt: 'int',
          hint: 'Billed here at $235 for a standard two-day convention ($200&ndash;$275 typical).' }
      ]
    },
    {
      n: '02',
      title: 'Routines &amp; costumes',
      note: 'The category that quietly doubles when your dancer gets a solo.',
      controls: [
        { id: 'groups', label: 'Group routines', min: 0, max: 12, step: 1, value: 3, fmt: 'int' },
        { id: 'duos', label: 'Duets and trios', min: 0, max: 4, step: 1, value: 0, fmt: 'int' },
        { id: 'solos', label: 'Solos', min: 0, max: 4, step: 1, value: 0, fmt: 'int' },
        { id: 'choreoGroup', label: 'Choreography fee, per group routine', min: 0, max: 600, step: 25, value: 175, fmt: 'usd' },
        { id: 'choreoDuo', label: 'Choreography fee, per duet or trio', min: 0, max: 1400, step: 50, value: 400, fmt: 'usd' },
        { id: 'choreoSolo', label: 'Choreography fee, per solo', min: 0, max: 2400, step: 50, value: 650, fmt: 'usd',
          hint: 'If your studio bills one all-in package per routine instead ($725&ndash;$2,250 is a documented range), put the package price here and set entry fees in section 03 to zero.' },
        { id: 'costumeGroup', label: 'Costume, per group routine', min: 60, max: 600, step: 10, value: 250, fmt: 'usd',
          hint: 'Group costumes are commonly $250&ndash;$400 each.' },
        { id: 'costumeSolo', label: 'Costume, per solo / duet / trio', min: 80, max: 800, step: 10, value: 325, fmt: 'usd',
          hint: 'Solo and small-group costumes range roughly $100&ndash;$500.' },
        { id: 'alterations', label: 'Alterations and rhinestoning, per costume', min: 0, max: 200, step: 5, value: 25, fmt: 'usd' }
      ]
    },
    {
      n: '03',
      title: 'Competition entry fees',
      note: 'Charged per routine, per competition, per dancer. This is where the arithmetic gets loud.',
      controls: [
        { id: 'comps', label: 'Local and regional competitions', min: 0, max: 10, step: 1, value: 3, fmt: 'int' },
        { id: 'entryGroup', label: 'Entry fee, per group routine, per competition', min: 30, max: 180, step: 5, value: 70, fmt: 'usd',
          hint: 'Regional group entries commonly land around $50&ndash;$150 per routine.' },
        { id: 'entryDuo', label: 'Entry fee, per duet or trio, per competition', min: 50, max: 250, step: 5, value: 110, fmt: 'usd' },
        { id: 'entrySolo', label: 'Entry fee, per solo, per competition', min: 60, max: 350, step: 10, value: 150, fmt: 'usd',
          hint: 'Solo entries range from about $80 to $300.' },
        { id: 'nationals', label: 'Going to nationals', type: 'switch', value: 0,
          hint: 'Turn this off for a local-only season.' },
        { id: 'natEntry', label: 'Nationals entry fees, total', min: 500, max: 6000, step: 100, value: 2500, fmt: 'usd', sub: 'nationals',
          hint: 'Nationals entry packages run roughly $2,000&ndash;$5,000.' },
        { id: 'natQual', label: 'Qualifier or regional-to-nationals costs', min: 0, max: 1500, step: 50, value: 700, fmt: 'usd', sub: 'nationals' }
      ]
    },
    {
      n: '04',
      title: 'Travel and the weekend itself',
      note: 'Counted once for the household, not per dancer.',
      controls: [
        { id: 'hotelNights', label: 'Hotel nights across the season', min: 0, max: 24, step: 1, value: 4, fmt: 'int' },
        { id: 'hotelRate', label: 'Average nightly rate', min: 100, max: 350, step: 5, value: 175, fmt: 'usd',
          hint: 'Competition-block hotel rates commonly run $125&ndash;$300 a night.' },
        { id: 'people', label: 'People traveling with you', min: 1, max: 8, step: 1, value: 3, fmt: 'int' },
        { id: 'foodDay', label: 'Food, per person, per travel day', min: 25, max: 175, step: 5, value: 90, fmt: 'usd',
          hint: 'Reported food costs on competition days run $75&ndash;$150 per person.' },
        { id: 'flights', label: 'Round-trip flights booked this season', min: 0, max: 10, step: 1, value: 0, fmt: 'int',
          hint: 'Billed here at $650 each; reported airfare runs $500&ndash;$1,000 per person.' },
        { id: 'gasComp', label: 'Gas, tolls and parking, per local competition', min: 0, max: 250, step: 10, value: 60, fmt: 'usd' }
      ]
    },
    {
      n: '05',
      title: 'The part nobody counts',
      note: 'Every line here is real, recurring, and almost never in the number a studio quotes you in August.',
      controls: [
        { id: 'shoes', label: 'Shoes, all styles, per dancer per year', min: 0, max: 700, step: 10, value: 250, fmt: 'usd',
          hint: 'One documented family totalled $517 across jazz, tap, ballet and lyrical in a single year.' },
        { id: 'tights', label: 'Tights, undergarments and warmups', min: 0, max: 500, step: 10, value: 150, fmt: 'usd' },
        { id: 'beauty', label: 'Competition makeup, hair and nails, per season', min: 0, max: 500, step: 10, value: 150, fmt: 'usd',
          hint: 'Commonly $100&ndash;$200 for the season.' },
        { id: 'spirit', label: 'Spirit wear, team jacket and warmup set', min: 0, max: 500, step: 10, value: 125, fmt: 'usd' },
        { id: 'teamFee', label: 'Team, recital or production fee', min: 0, max: 800, step: 25, value: 150, fmt: 'usd' },
        { id: 'photo', label: 'Photo and video package, per competition', min: 0, max: 350, step: 10, value: 120, fmt: 'usd',
          hint: 'Reported at $50&ndash;$300 per competition.' },
        { id: 'tickets', label: 'Family admission tickets, per competition', min: 0, max: 300, step: 10, value: 80, fmt: 'usd' }
      ]
    }
  ];

  var PRIVATE_RATE = 45;
  var CONVENTION_RATE = 235;
  var FLIGHT_RATE = 650;
  var NATIONAL_AVG = 7750;      /* midpoint of the documented $7,500-$8,000 */
  var YOUTH_SPORT_AVG = 1015.64;

  /* ---------------- Build the DOM ---------------- */
  var state = {};
  var form = document.getElementById('calc-form');

  function fmt(v, kind) {
    if (kind === 'usd') return usd(v);
    if (kind === 'mo') return v + (v === 1 ? ' month' : ' months');
    return String(v);
  }

  function controlHTML(c) {
    if (c.type === 'switch') {
      return '' +
        '<div class="ctl">' +
          '<div class="ctl-switch">' +
            '<div>' +
              '<span class="ctl-label" id="' + c.id + '-label">' + c.label + '</span>' +
              (c.hint ? '<p class="ctl-hint">' + c.hint + '</p>' : '') +
            '</div>' +
            '<button type="button" class="ct-switch" role="switch" id="' + c.id + '" data-switch="' + c.id + '" ' +
              'aria-checked="' + (c.value ? 'true' : 'false') + '" aria-labelledby="' + c.id + '-label"></button>' +
          '</div>' +
        '</div>';
    }
    return '' +
      '<div class="ctl"' + (c.sub ? ' data-sub-of="' + c.sub + '"' : '') + '>' +
        '<div class="ctl-head">' +
          '<label class="ctl-label" for="' + c.id + '">' + c.label + '</label>' +
          '<output class="ctl-out num" id="' + c.id + '-out" for="' + c.id + '">' + fmt(c.value, c.fmt) + '</output>' +
        '</div>' +
        '<input type="range" id="' + c.id + '" data-range="' + c.id + '" data-fmt="' + c.fmt + '" ' +
          'min="' + c.min + '" max="' + c.max + '" step="' + c.step + '" value="' + c.value + '" ' +
          'aria-describedby="' + (c.hint ? c.id + '-hint ' : '') + c.id + '-out">' +
        (c.hint ? '<p class="ctl-hint" id="' + c.id + '-hint">' + c.hint + '</p>' : '') +
      '</div>';
  }

  var html = '';
  GROUPS.forEach(function (g) {
    var mains = g.controls.filter(function (c) { return !c.sub; });
    var subs = g.controls.filter(function (c) { return c.sub; });

    html += '<fieldset class="calc-group">' +
      '<legend><span class="calc-group-n">' + g.n + '</span> <span class="calc-group-title">' + g.title + '</span></legend>' +
      '<p class="calc-group-note">' + g.note + '</p>';
    mains.forEach(function (c) { html += controlHTML(c); });
    if (subs.length) {
      var gate = mains.filter(function (c) { return c.type === 'switch'; })[0];
      html += '<div class="subgroup" id="sub-nationals"' + (gate && gate.value ? '' : ' hidden') + '>';
      subs.forEach(function (c) { html += controlHTML(c); });
      html += '</div>';
    }
    html += '<p class="calc-group-total"><span class="muted">Section subtotal</span> <b class="num" id="sub-' + g.n + '">$0</b></p>' +
      '</fieldset>';
  });
  form.innerHTML = html;

  /* seed state */
  GROUPS.forEach(function (g) {
    g.controls.forEach(function (c) { state[c.id] = c.value; });
  });

  /* ---------------- Compute ---------------- */
  function compute() {
    var s = state;
    var d = s.dancers;
    var routines = s.groups + s.duos + s.solos;
    var costumeCount = routines;

    /* 01 Training, per dancer */
    var training = (s.tuition * s.months) +
                   (s.privates * s.months * PRIVATE_RATE) +
                   (s.conventions * CONVENTION_RATE);
    training *= d;

    /* 02 Routines and costumes, per dancer */
    var costumes = (s.groups * (s.choreoGroup + s.costumeGroup)) +
                   (s.duos * (s.choreoDuo + s.costumeSolo)) +
                   (s.solos * (s.choreoSolo + s.costumeSolo)) +
                   (costumeCount * s.alterations);
    costumes *= d;

    /* 03 Entry fees, per dancer */
    var entries = s.comps * ((s.groups * s.entryGroup) + (s.duos * s.entryDuo) + (s.solos * s.entrySolo));
    if (s.nationals) entries += s.natEntry + s.natQual;
    entries *= d;

    /* 04 Travel, household */
    var travel = (s.hotelNights * s.hotelRate) +
                 (s.hotelNights * s.people * s.foodDay) +
                 (s.flights * FLIGHT_RATE) +
                 (s.comps * s.gasComp);

    /* 05 Gear and extras: gear per dancer, tickets and photo per household */
    var gearPerDancer = (s.shoes + s.tights + s.beauty + s.spirit + s.teamFee) * d;
    var events = s.comps + (s.nationals ? 1 : 0);
    var extrasHousehold = events * (s.photo + s.tickets);
    var extras = gearPerDancer + extrasHousehold;

    var total = training + costumes + entries + travel + extras;

    return {
      total: total,
      routines: routines * d,
      cats: [
        { name: 'Studio &amp; training', v: training, sub: '01' },
        { name: 'Routines &amp; costumes', v: costumes, sub: '02' },
        { name: 'Entry fees', v: entries, sub: '03' },
        { name: 'Travel &amp; the weekend', v: travel, sub: '04' },
        { name: 'Gear, beauty &amp; extras', v: extras, sub: '05' }
      ]
    };
  }

  /* ---------------- Animated number ---------------- */
  var animFrom = 0, animRAF = null;
  var totalEl = document.getElementById('result-total');
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function animateTotal(to) {
    if (reduce) { totalEl.textContent = usdc(to); animFrom = to; return; }
    if (animRAF) cancelAnimationFrame(animRAF);
    var from = animFrom, start = performance.now(), dur = 460;
    function step(now) {
      var t = Math.min(1, (now - start) / dur);
      var e = 1 - Math.pow(1 - t, 3);
      totalEl.textContent = usdc(from + (to - from) * e);
      if (t < 1) { animRAF = requestAnimationFrame(step); } else { animFrom = to; animRAF = null; }
    }
    animRAF = requestAnimationFrame(step);
  }

  /* ---------------- Render ---------------- */
  var bdWrap = document.getElementById('breakdown');
  var bdBuilt = false;

  function render() {
    var r = compute();

    animateTotal(r.total);

    var stickyEl = document.getElementById('sticky-total');
    if (stickyEl) stickyEl.textContent = usdc(r.total);

    document.getElementById('mini-month').textContent = usdc(r.total / 12);
    document.getElementById('mini-routine').textContent = r.routines ? usdc(r.total / r.routines) : 'n/a';
    document.getElementById('mini-dancer').textContent = usdc(r.total / state.dancers);

    /* section subtotals */
    r.cats.forEach(function (c) {
      var el = document.getElementById('sub-' + c.sub);
      if (el) el.textContent = usdc(c.v);
    });

    /* breakdown */
    var max = Math.max.apply(null, r.cats.map(function (c) { return c.v; })) || 1;
    if (!bdBuilt) {
      bdWrap.innerHTML = r.cats.map(function (c, i) {
        return '<div class="bd-row">' +
          '<div class="bd-head"><span class="bd-name">' + c.name + '</span>' +
          '<span><span class="bd-val" id="bdv-' + i + '">$0</span><span class="bd-pct" id="bdp-' + i + '">0%</span></span></div>' +
          '<div class="bd-track"><div class="bd-fill" id="bdf-' + i + '"></div></div></div>';
      }).join('');
      bdBuilt = true;
    }
    r.cats.forEach(function (c, i) {
      document.getElementById('bdv-' + i).textContent = usdc(c.v);
      document.getElementById('bdp-' + i).textContent = r.total ? Math.round((c.v / r.total) * 100) + '%' : '0%';
      document.getElementById('bdf-' + i).style.width = ((c.v / max) * 100).toFixed(1) + '%';
    });

    /* comparisons */
    var perDancer = r.total / state.dancers;
    var vsAvg = perDancer / NATIONAL_AVG;
    var vsSport = perDancer / YOUTH_SPORT_AVG;
    var diff = perDancer - NATIONAL_AVG;

    document.getElementById('cmp-avg').innerHTML = diff >= 0
      ? 'Your per-dancer total is <b>' + usdc(Math.abs(diff)) + ' above</b> the $7,750 midpoint documented in the Brock University dance-parent survey, or about <b>' + vsAvg.toFixed(2) + '&times;</b> the reported average.'
      : 'Your per-dancer total is <b>' + usdc(Math.abs(diff)) + ' below</b> the $7,750 midpoint documented in the Brock University dance-parent survey, or about <b>' + vsAvg.toFixed(2) + '&times;</b> the reported average.';

    document.getElementById('cmp-sport').innerHTML =
      'That is <b>' + vsSport.toFixed(1) + '&times;</b> what the average U.S. family spends per child per year across all youth sports ($1,015.64).';

    var hiddenShare = r.total ? (r.cats[4].v / r.total) * 100 : 0;
    document.getElementById('cmp-hidden').innerHTML =
      '<b>' + usdc(r.cats[4].v) + '</b> of your season, ' + Math.round(hiddenShare) +
      '% of it, sits in gear, beauty, tickets and photo packages. That is the part almost no studio quotes you in August.';
  }

  /* ---------------- Wire up ---------------- */
  form.addEventListener('input', function (e) {
    var id = e.target.getAttribute('data-range');
    if (!id) return;
    var val = Number(e.target.value);
    state[id] = val;
    var out = document.getElementById(id + '-out');
    if (out) out.textContent = fmt(val, e.target.getAttribute('data-fmt'));
    render();
  });

  form.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-switch]');
    if (!btn) return;
    var on = btn.getAttribute('aria-checked') !== 'true';
    btn.setAttribute('aria-checked', String(on));
    state[btn.getAttribute('data-switch')] = on ? 1 : 0;
    document.getElementById('sub-nationals').hidden = !on;
    render();
  });

  document.getElementById('calc-reset').addEventListener('click', function () {
    GROUPS.forEach(function (g) {
      g.controls.forEach(function (c) {
        state[c.id] = c.value;
        var el = document.getElementById(c.id);
        if (!el) return;
        if (c.type === 'switch') {
          el.setAttribute('aria-checked', c.value ? 'true' : 'false');
          document.getElementById('sub-nationals').hidden = !c.value;
        } else {
          el.value = c.value;
          var out = document.getElementById(c.id + '-out');
          if (out) out.textContent = fmt(c.value, c.fmt);
        }
      });
    });
    render();
  });

  document.body.classList.add('has-calc-bar');
  render();
})();
